// src/components/financials/TickerFinancialsSummary.jsx
// SCREENER-FINANCIALS — rewritten this pass to match the Filings tab's own
// standard, per explicit request: group periods (Annual/Quarterly, mirrors
// TickerFilingsSummary.jsx's group-by-label design) with one EDGAR
// direct-search link per group, and format all dollar figures compactly
// (K/M/B) instead of full digit strings.

import React from 'react';
import { useTickerFinancialsSummary } from '../../hooks/useTickerFinancialsSummary';
import { useAmendmentDiff } from '../../hooks/useAmendmentDiff';
import IncomeStatementSimple from './IncomeStatementSimple';
import BalanceSheetSimple from './BalanceSheetSimple';
import CashFlowSimple from './CashFlowSimple';
import AmendmentDiff from './AmendmentDiff';

// Compact number formatting — $ figures only. Ratios/percentages/EPS stay
// full-precision (fmtRatio/fmtPct below) since compacting a per-share value
// or a ratio would misrepresent it, not just shorten it.
function fmtCompact(n) {
  if (n == null) return '—';
  const abs = Math.abs(Number(n));
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return sign + '$' + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return sign + '$' + (abs / 1e6).toFixed(2) + 'M';
  if (abs >= 1e3) return sign + '$' + (abs / 1e3).toFixed(1) + 'K';
  return sign + '$' + abs.toFixed(0);
}
function fmtRatio(n) {
  if (n == null) return '—';
  return Number(n).toFixed(2);
}
function fmtPct(n) {
  if (n == null) return '—';
  return (Number(n) * 100).toFixed(1) + '%';
}

function StatRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0',
      borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: '0.82rem', color: '#f8fafc', fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function PeriodCard({ period, priorPeriod, ticker }) {
  const { statements: s, computed: c } = period;
  const [viewMode, setViewMode] = React.useState('simple'); // 'simple' | 'full' — Simple is the default, non-accountant-friendly view
  const [diffExpanded, setDiffExpanded] = React.useState(false);
  const { data: diffData, loading: diffLoading, error: diffError, fetchDiff } = useAmendmentDiff();

  const toggleDiff = () => {
    const next = !diffExpanded;
    setDiffExpanded(next);
    if (next && !diffData) fetchDiff(ticker, period.fiscalYear, period.fiscalPeriod);
  };

  return (
    <div style={{ marginBottom: '1.25rem', padding: '1.1rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.9rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#f8fafc' }}>
          {period.fiscalPeriod === 'FY' ? 'FY' + period.fiscalYear : period.fiscalPeriod + ' ' + period.fiscalYear}
        </span>
        <span style={{ fontSize: '0.72rem', color: '#475569' }}>{period.formType}</span>
        <span style={{ fontSize: '0.72rem', color: '#475569' }}>Filed {period.filedDate}</span>
        {period.isAmendment && (
          <button onClick={toggleDiff}
            title={period.amendsPriorFiling ? 'Amends a previously filed report for this period — click to see what changed' : 'Amended filing — click to see what changed'}
            style={{ fontSize: '0.65rem', fontWeight: 700, color: '#ffaa00',
            background: 'rgba(255,170,0,0.1)', border: '1px solid rgba(255,170,0,0.3)',
            borderRadius: '12px', padding: '0.15rem 0.55rem', cursor: 'pointer', fontFamily: 'inherit' }}>
            ⚠ Amended {diffExpanded ? '▲' : '▼'}
          </button>
        )}
      </div>

      {period.isAmendment && diffExpanded && (
        <div style={{ marginBottom: '1rem' }}>
          <AmendmentDiff diff={diffData} loading={diffLoading} error={diffError} />
        </div>
      )}

      {/* Computed ratios/growth — primary, inline-visible per design decision */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '0.6rem', marginBottom: '1rem' }}>
        <StatRow label="Gross Margin" value={fmtPct(c.grossMargin)} />
        <StatRow label="Net Margin" value={fmtPct(c.netProfitMargin)} />
        <StatRow label="ROE" value={fmtPct(c.returnOnEquity)} />
        <StatRow label="ROA" value={fmtPct(c.returnOnAssets)} />
        <StatRow label="Debt/Equity" value={fmtRatio(c.debtToEquity)} />
        <StatRow label="Current Ratio" value={fmtRatio(c.currentRatio)} />
        <StatRow label="EPS Growth QoQ" value={fmtPct(c.epsGrowthQoQ)} />
        <StatRow label="Sales Growth QoQ" value={fmtPct(c.salesGrowthQoQ)} />
      </div>

      {/* Simple/Full toggle — the differentiation lever. Every competitor
          (Macrotrends/StockAnalysis/WSJ/Finviz) only ever shows the Full
          equivalent below as a plain number table. Simple is the default. */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
        <button onClick={() => setViewMode('simple')}
          style={{ padding: '0.35rem 0.85rem', borderRadius: '8px', fontSize: '0.72rem', fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit', border: '1px solid ' + (viewMode === 'simple' ? '#00d4ff' : 'rgba(255,255,255,0.1)'),
            background: viewMode === 'simple' ? 'rgba(0,212,255,0.1)' : 'transparent',
            color: viewMode === 'simple' ? '#00d4ff' : '#64748b' }}>
          Simple View
        </button>
        <button onClick={() => setViewMode('full')}
          style={{ padding: '0.35rem 0.85rem', borderRadius: '8px', fontSize: '0.72rem', fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit', border: '1px solid ' + (viewMode === 'full' ? '#00d4ff' : 'rgba(255,255,255,0.1)'),
            background: viewMode === 'full' ? 'rgba(0,212,255,0.1)' : 'transparent',
            color: viewMode === 'full' ? '#00d4ff' : '#64748b' }}>
          Full Statement
        </button>
      </div>

      {viewMode === 'simple' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Income Statement</div>
            <IncomeStatementSimple statements={s} priorStatements={priorPeriod?.statements} />
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Balance Sheet</div>
            <BalanceSheetSimple statements={s} />
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Cash Flow</div>
            <CashFlowSimple statements={s} computed={c} />
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 1.5rem' }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', marginBottom: '0.4rem' }}>INCOME STATEMENT</div>
            <StatRow label="Revenue" value={fmtCompact(s.revenue)} />
            <StatRow label="Gross Profit" value={fmtCompact(s.grossProfit)} />
            <StatRow label="Operating Income" value={fmtCompact(s.operatingIncome)} />
            <StatRow label="Net Income" value={fmtCompact(s.netIncome)} />
            <StatRow label="EPS (Diluted)" value={s.epsDiluted != null ? '$' + Number(s.epsDiluted).toFixed(2) : '—'} />
          </div>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', marginBottom: '0.4rem' }}>BALANCE SHEET</div>
            <StatRow label="Total Assets" value={fmtCompact(s.totalAssets)} />
            <StatRow label="Total Liabilities" value={fmtCompact(s.totalLiabilities)} />
            <StatRow label="Stockholders' Equity" value={fmtCompact(s.totalStockholdersEquity)} />
            <StatRow label="Cash & Equivalents" value={fmtCompact(s.cashAndEquivalents)} />
          </div>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', marginBottom: '0.4rem' }}>CASH FLOW</div>
            <StatRow label="Operating Cash Flow" value={fmtCompact(s.operatingCashFlow)} />
            <StatRow label="CapEx" value={fmtCompact(s.capex)} />
            <StatRow label="Free Cash Flow" value={fmtCompact(c.freeCashFlow)} />
            <StatRow label="Dividends/Share" value={s.dividendsPerShare != null ? '$' + Number(s.dividendsPerShare).toFixed(2) : '—'} />
          </div>
        </div>
      )}
    </div>
  );
}

function PeriodGroup({ group, ticker }) {
  // Collapsible (this session, real gap found in production — a company with
  // deep filing history renders 30+ years of cards, an unusable wall of
  // scrolling by default). Quarterly starts expanded (most members want the
  // freshest number first); Annual starts collapsed (comprehensive reference,
  // not the first thing reached for) — matches this file's own group ORDER
  // in financialsQueryService.js.
  const [expanded, setExpanded] = React.useState(group.label === 'Quarterly Reports');

  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: expanded ? '0.75rem' : 0, flexWrap: 'wrap', gap: '0.5rem',
          cursor: 'pointer', padding: '0.6rem 0.75rem', background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#64748b', transition: 'transform 0.15s',
            display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
            ▶
          </span>
          <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#f8fafc' }}>{group.label}</span>
          <span style={{ fontSize: '0.72rem', color: '#475569' }}>({group.periods.length})</span>
        </div>
        <a href={group.edgarUrl} target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          style={{ fontSize: '0.72rem', color: '#00d4ff', textDecoration: 'none' }}>
          EDGAR Direct Search: {group.formTypeForSearch} — Older Forms →
        </a>
      </div>
      {expanded && group.periods.map((p, i) => (
        <PeriodCard key={p.fiscalYear + '-' + p.fiscalPeriod + '-' + i} period={p} priorPeriod={group.periods[i + 1]} ticker={ticker} />
      ))}
    </div>
  );
}

export default function TickerFinancialsSummary({ ticker }) {
  const { groups, loading, error } = useTickerFinancialsSummary(ticker);

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading financials…</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '2rem', color: '#ff4444' }}>{error}</div>;

  if (groups.length === 0) {
    return (
      <div style={{ padding: '1.5rem', textAlign: 'center', color: '#475569', fontSize: '0.85rem' }}>
        No XBRL financial statements indexed yet for this company.
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: '0.68rem', color: '#475569', marginBottom: '1.25rem' }}>
        * Sourced directly from SEC EDGAR XBRL filings. Ratios and growth rates are computed
        from reported figures, not analyst estimates.
      </div>
      {groups.map(g => <PeriodGroup key={g.label} group={g} ticker={ticker} />)}
    </div>
  );
}
